import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Global default timeout — 30 s covers the vast majority of endpoints. The
// role-play turn call overrides this to 45 s (Claude Haiku can occasionally
// take 6-10 s on cold pods and we don't want to fail a live conversation on
// a slightly slow turn). Any request exceeding its budget rejects with
// axios ECONNABORTED, which formatApiError() maps to a friendly retry copy.
const api = axios.create({ baseURL: API, timeout: 30000 });

/**
 * Create a per-request AbortController + timeout. Use for long-running
 * calls like role-play turn where we want to cancel a stalled request and
 * fall back to a recovery path.
 *
 * Usage:
 *   const { signal, cancel } = createAbortSignal(45000);
 *   try { await api.post(url, body, { signal }); }
 *   finally { cancel(); }
 */
export const createAbortSignal = (timeoutMs = 45000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(new Error("Client timeout")), timeoutMs);
  return {
    signal: controller.signal,
    cancel: () => clearTimeout(timer),
  };
};

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("vi_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (e) => {
    if (e.response?.status === 401) {
      localStorage.removeItem("vi_token");
      localStorage.removeItem("vi_user");
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }
    return Promise.reject(e);
  }
);

export default api;

export const formatApiError = (err) => {
  // Server returned a JSON error body
  const detail = err?.response?.data?.detail;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((d) => d.msg || JSON.stringify(d)).join(" ");

  // Server returned a status without a useful body — translate it
  const status = err?.response?.status;
  if (status === 401) return "Your session expired. Please log in again.";
  if (status === 403) return "You don't have permission to do that.";
  if (status === 404) return "We couldn't find that. The server may be restarting — please try again in a few seconds.";
  if (status === 502 || status === 503 || status === 504) {
    return "The server is taking longer than usual to respond. Please try again in a moment.";
  }
  if (status === 408 || err?.code === "ECONNABORTED" || err?.name === "CanceledError" || err?.name === "AbortError") {
    return "The customer is taking too long to respond. Please try again — your last message was saved.";
  }
  if (!err?.response && err?.message === "Network Error") {
    return "Can't reach the server. Please check your internet connection and try again.";
  }
  return err?.message || "Something went wrong. Please try again.";
};
